import React, { useEffect } from "react";
import DefaultModalLayout from "../../../Modal/DefaultModalLayout";
import { DefaultModalCommonProps } from "./CommentModal/CommentModal";
import { useNavigate, useParams } from "react-router-dom";
import { PayHelper } from "../../../../scCommon/scCommonFunc";
import styled from "styled-components";
import moment from "moment";
import Flex from "../../../DefaultStyles/Flex";
import SpaceBetween from "../../../DefaultStyles/SpaceBetween";
import { GetMoneyValue } from "../../../../ApiTS/ApiCommonTS";
import { useApi } from "../../../../hooks/useApi";
import { useRecoilValue } from "recoil";
import { authType } from "../../../../pages/Login";
import Loader from "../../../DefaultStyles/Loader";
import { GetSubscriptionSetData } from "../../../../ApiTS/ServiceDatasTS";
import { toast } from "react-toastify";
import { getSubsNameBySubsId3 } from "../../../../AdminApi/3.0_utils";

interface UserPaydayPreviewModalProps extends DefaultModalCommonProps {
  payday: number;
}

interface INextBillData {
  amount: number;
  partyid: string;
  servsubsid: number;
}

export default function UserPaydayPreviewModal({
  closeEvent,
  confirmEvent,
  payday,
}: UserPaydayPreviewModalProps) {
  const { userId } = useParams();
  const auth = useRecoilValue(authType);
  const navigate = useNavigate();

  const nextPday = PayHelper.nextpdate(payday);
  const nextPdayString = moment(nextPday).format("YYYY.MM.DD");

  const { response, loading, error } = useApi({
    method: "get",
    url: auth?.userInfo ? `user/${userId}/nextbill` : undefined,
  });

  // amount 합계
  const getTotalAmount = (list: INextBillData[]) => {
    let sum = 0;

    list?.forEach((a) => {
      sum += a?.amount ?? 0;
    });

    return sum;
  };

  useEffect(() => {
    if (error) {
      toast.error(
        error?.data ?? "정산일 미리보기 정보를 가져오는 데에 실패하였습니다."
      );
    }
  }, [error]);

  return (
    <DefaultModalLayout
      modalTitle="정산일 미리보기"
      closeEvent={() => closeEvent()}
      confirmEvent={() => {
        if (confirmEvent) confirmEvent();
      }}
      cancelBtn={<></>}
      type="view"
      modalDesc={`오늘 기준 다음 링키드 정산일(${nextPdayString})의\n예상 결제/적립 내역입니다.`}
    >
      <>
        <Content>
          <Flex gapSize={0}>
            <NextPday>{nextPdayString}</NextPday>
            <PaydayTag>정산일</PaydayTag>
          </Flex>

          <>
            {getTotalAmount(response?.data?.allot) > 0 ||
            getTotalAmount(response?.data?.pay) > 0 ? (
              <>
                {getTotalAmount(response?.data?.allot) > 0 && (
                  <AllotSection>
                    <SpaceBetween>
                      <SectionTitle>파티 요금 적립</SectionTitle>
                      <TotalValue>
                        {GetMoneyValue(getTotalAmount(response?.data?.allot))}
                      </TotalValue>
                    </SpaceBetween>

                    <SectionDetail>
                      {response?.data?.allot?.map((d: INextBillData) => {
                        const subsName =
                          getSubsNameBySubsId3(d?.servsubsid ?? 0) ?? "";

                        if (d?.amount > 0) {
                          return (
                            <SectionDetailItem key={d?.partyid}>
                              <SubsName
                                onClick={() =>
                                  navigate(`/party/info/${d?.partyid}`)
                                }
                              >
                                {subsName}({d?.partyid})
                              </SubsName>
                              <Value>{GetMoneyValue(d?.amount ?? 0)}</Value>
                            </SectionDetailItem>
                          );
                        } else {
                          return null;
                        }
                      })}
                    </SectionDetail>
                  </AllotSection>
                )}

                {getTotalAmount(response?.data?.pay) > 0 && (
                  <PaySection>
                    <SpaceBetween>
                      <SectionTitle>파티 요금 결제</SectionTitle>
                      <TotalValue>
                        {GetMoneyValue(getTotalAmount(response?.data?.pay))}
                      </TotalValue>
                    </SpaceBetween>

                    <SectionDetail>
                      {response?.data?.pay?.map((d: INextBillData) => {
                        const subsName =
                          getSubsNameBySubsId3(d?.servsubsid ?? 0) ?? "";

                        if (d?.amount > 0) {
                          return (
                            <SectionDetailItem key={d?.partyid}>
                              <SubsName
                                onClick={() =>
                                  navigate(`/party/info/${d?.partyid}`)
                                }
                              >
                                {subsName}({d?.partyid})
                              </SubsName>
                              <Value>{GetMoneyValue(d?.amount ?? 0)}</Value>
                            </SectionDetailItem>
                          );
                        } else {
                          return null;
                        }
                      })}
                    </SectionDetail>
                  </PaySection>
                )}
              </>
            ) : (
              <NothingToDisplay>표시할 내역이 없습니다.</NothingToDisplay>
            )}
          </>
        </Content>

        {loading && <Loader />}
      </>
    </DefaultModalLayout>
  );
}

const Content = styled.div`
  width: 100%;
  border-top: 8px solid #efefef;
  border-bottom: 8px solid #efefef;
  padding: 20px 0;
  margin-bottom: 48px;
`;

const NextPday = styled.span`
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
  text-align: left;
`;

const PaydayTag = styled.span`
  margin-left: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  border-radius: 5px;
  background-color: #f0f0ff;
  padding: 0 6px;

  font-size: 12px;
  font-weight: bold;
  line-height: 21px;
  text-align: left;
  color: #6103ce;
`;

const AllotSection = styled.div`
  width: 100%;
  border-top: 1px solid #e6e6e6;
  margin-top: 16px;
  padding: 24px 0;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
`;

const SectionDetail = styled.div`
  margin: 14px 0 0 16px;
  width: calc(100% - 16px);
`;

const SectionDetailItem = styled(SpaceBetween)`
  &:not(:last-child) {
    margin-bottom: 14px;
  }
`;

const SubsName = styled.span`
  cursor: pointer;
  font-size: 14px;
  line-height: 21px;
  color: #656565;
  text-decoration: underline;
`;

const Value = styled.span`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #656565;
`;

const TotalValue = styled.span`
  font-size: 18px;
  font-weight: bold;
  line-height: 27px;
  color: #222222;
`;

const PaySection = styled(AllotSection)``;

const NothingToDisplay = styled.div`
  margin: 60px 0;
  font-size: 13px;
  line-height: 19.5px;
  text-align: center;
  color: #8b8b8b;
`;
